import { RING_BUFFER_SIZE } from './bytecode.ts'
import { dspscript } from './dsp-script.ts'

interface DelayBuffer {
  left: Float32Array
  right: Float32Array
  writePtr: number
  readPtr: number
  fillLevel: number
}

class DelayBufferManager {
  private delayBuffers: Map<string, DelayBuffer> = new Map()
  private ringAnalyserReadPtr = 0
  private initialized = false

  getOrCreateDelayBuffer(id: string, size: number = 16384): DelayBuffer {
    if (!this.delayBuffers.has(id)) {
      this.delayBuffers.set(id, {
        left: new Float32Array(size),
        right: new Float32Array(size),
        writePtr: 0,
        readPtr: 0,
        fillLevel: 0,
      })
    }
    return this.delayBuffers.get(id)!
  }

  update(): void {
    const ringAnalyser = dspscript.getRingAnalyser()
    if (!ringAnalyser) return

    // Access ring analyser's internal buffers via reflection
    const ringAnalyserAny = ringAnalyser as any
    const leftBuffer = ringAnalyserAny.leftBuffer as Float32Array
    const rightBuffer = ringAnalyserAny.rightBuffer as Float32Array
    const writePtr = ringAnalyserAny.writePtr as Int32Array
    const bufferSize = RING_BUFFER_SIZE
    const safetyLag = 2048

    if (!leftBuffer || !rightBuffer || !writePtr) return

    // Initialize read pointer on first update
    if (!this.initialized) {
      const currentWritePtr = Atomics.load(writePtr, 0)
      this.ringAnalyserReadPtr = (currentWritePtr - safetyLag + bufferSize) % bufferSize
      this.initialized = true
    }

    // Get available samples from ring analyser
    const currentWritePtr = Atomics.load(writePtr, 0)
    const distance = (currentWritePtr - this.ringAnalyserReadPtr + bufferSize) % bufferSize
    const availableSamples = Math.max(0, distance - safetyLag)
    const availableWithCap = Math.min(availableSamples, bufferSize / 2)

    if (availableWithCap <= 0) return

    // Distribute samples to all delay buffers (each gets the same samples)
    for (const [id, delayBuffer] of this.delayBuffers.entries()) {
      const delayBufferSize = delayBuffer.left.length
      // Add samples up to buffer capacity, wrapping if needed
      const samplesToAdd = Math.min(availableWithCap, delayBufferSize)

      for (let s = 0; s < samplesToAdd; s++) {
        const readIdx = (this.ringAnalyserReadPtr + s) % bufferSize
        const writeIdx = delayBuffer.writePtr

        delayBuffer.left[writeIdx] = leftBuffer[readIdx]!
        delayBuffer.right[writeIdx] = rightBuffer[readIdx]!

        delayBuffer.writePtr = (writeIdx + 1) % delayBufferSize
      }

      // Recalculate fill level based on actual distance between read and write pointers
      const distance = (delayBuffer.writePtr - delayBuffer.readPtr + delayBufferSize) % delayBufferSize
      delayBuffer.fillLevel = Math.min(distance, delayBufferSize)
    }

    // Advance ring analyser read pointer
    this.ringAnalyserReadPtr = (this.ringAnalyserReadPtr + availableWithCap) % bufferSize
  }

  reset(): void {
    this.initialized = false
    this.ringAnalyserReadPtr = 0
    for (const delayBuffer of this.delayBuffers.values()) {
      delayBuffer.writePtr = 0
      delayBuffer.readPtr = 0
      delayBuffer.fillLevel = 0
    }
  }

  removeDelayBuffer(id: string): void {
    this.delayBuffers.delete(id)
  }
}

export const delayBufferManager = new DelayBufferManager()
